// A creative as one of its revisions, for the surfaces that show a pinned or
// approved version rather than the library's current one.
//
// The library `Creative` is the header and the mirror of its *latest*
// revision (0023): once an edit has minted a draft, its `assets` and `spec`
// are the draft's. A campaign slot flies the revision it pinned and an
// addition adds the approved one, so a card labelled "Applied version 1" drew
// version 2's artwork and the add drawer showed a draft's copy beside "version
// 1 is the one added" (audit, 2026-09-18). When the wanted revision is not the
// current one, this reads the archive and answers with that snapshot's
// content on the same header; while the archive loads it answers nothing, so
// a caller draws its initials fallback and never the wrong artwork.

import { useCreativeRevisions } from '@/api/manage-hooks';
import type { Creative } from '@/api/types';

export interface CreativeAtRevision {
  /** The creative with that revision's content, or undefined while unknown. */
  creative: Creative | undefined;
  /** True while the archive is still being read. */
  pending: boolean;
}

export function useCreativeAtRevision(
  creative: Creative | undefined,
  revision: number | null | undefined,
): CreativeAtRevision {
  const differs = !!creative && revision != null && revision !== creative.revision;
  const revisions = useCreativeRevisions(creative?.id ?? '', { enabled: differs });
  if (!creative || !differs) return { creative, pending: false };
  const snapshot = revisions.data?.find((row) => row.revision === revision);
  if (!snapshot) return { creative: undefined, pending: revisions.isPending };
  return {
    creative: {
      ...creative,
      revision: snapshot.revision,
      spec: snapshot.spec,
      destination_url: snapshot.destination_url,
      assets: snapshot.assets,
    },
    pending: false,
  };
}
